import { LoginForm } from '@/components/auth/LoginForm';
import { getCachedCustomDomainOrg, getEnvBaseDomain, isCustomDomain } from '@/core/domain';
import { attemptHandoff } from '@/core/handoff';
import { getToken } from '@/core/token';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function LoginPage() {
  // Central-login fast path: an already-authenticated user deep-linking in
  // from a custom domain (bounced here with ?returnTo=<custom-domain URL> by
  // the effect below, running on that domain) shouldn't have to see the
  // login form again — they're already logged in on skygis.app. Attempt the
  // handoff immediately and show a spinner while it's in flight; fall
  // through to the normal form if it fails (expired session, not a member,
  // network error) — the existing post-login tryHandoff in LoginForm covers
  // that case once they do log in.
  const [handoffPending, setHandoffPending] = useState(
    () => !isCustomDomain() && !!new URLSearchParams(window.location.search).get('returnTo') && !!getToken(),
  );

  // Custom domains can't run the login flow themselves (no way to read/write
  // `.skygis.app` auth cookies or complete the OTP/password/passkey round trip
  // against a foreign origin) — bounce to the central login on skygis.app and
  // carry a returnTo so LoginForm can hand the fresh session back afterwards.
  useEffect(() => {
    if (!isCustomDomain()) return;
    const org = getCachedCustomDomainOrg();
    if (!org) return; // unresolved custom domain — main.tsx shows the neutral page
    const returnTo = encodeURIComponent(window.location.origin + '/');
    window.location.replace(`https://${org.slug}.${getEnvBaseDomain()}/auth/login?returnTo=${returnTo}`);
  }, []);

  useEffect(() => {
    if (!handoffPending) return;
    const returnTo = new URLSearchParams(window.location.search).get('returnTo');
    if (!returnTo) {
      setHandoffPending(false);
      return;
    }
    let cancelled = false;
    attemptHandoff(returnTo).then((ok) => {
      // On success the browser is already navigating away; on failure, drop
      // the spinner and render the normal login form.
      if (!cancelled && !ok) setHandoffPending(false);
    });
    return () => {
      cancelled = true;
    };
  }, [handoffPending]);

  if (isCustomDomain()) return null;

  if (handoffPending) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Loader2 className='h-6 w-6 animate-spin' />
      </div>
    );
  }

  return (
    <div className='bg-slate-50 dark:bg-stone-950 flex min-h-svh flex-col items-center justify-center p-6 md:p-10'>
      <div className='w-full max-w-sm md:max-w-3xl'>
        <LoginForm />
      </div>
    </div>
  );
}
